import { useClickOutside } from './useClickOutSide';
import React, { useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const Portal = ({
  isOpen,
  children,
  triggerRef,
  placement = 'auto',
  offset = 4,
  zIndex = 50,
  onClose,
}) => {
  const [portalRoot, setPortalRoot] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const childRef = React.useRef(null);

  useClickOutside([triggerRef, childRef], () => {
    onClose();
  });

  // Create and manage portal root element
  useEffect(() => {
    // Find the root element (typically where your React app is mounted)
    const rootElement = document.getElementById('root') || document.body;

    // Create portal container if it doesn't exist
    let portalContainer = document.getElementById('portal-root');
    if (!portalContainer) {
      portalContainer = document.createElement('div');
      portalContainer.id = 'portal-root';
      rootElement.appendChild(portalContainer);
    }

    setPortalRoot(portalContainer);

    // Cleanup
    return () => {
      if (portalContainer && portalContainer.childNodes.length === 0) {
        portalContainer.remove();
      }
    };
  }, []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !childRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const childRect = childRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    // On mobile, center the calendar in the viewport
    if (isMobile) {
      const newPosition = {
        top: (window.innerHeight - childRect.height) / 2,
        left: (window.innerWidth - childRect.width) / 2,
      };

      // Ensure minimum margins on mobile
      newPosition.top = Math.max(20, newPosition.top);
      newPosition.left = Math.max(20, newPosition.left);

      // Ensure it doesn't go off the bottom or right
      if (newPosition.top + childRect.height > window.innerHeight - 20) {
        newPosition.top = window.innerHeight - childRect.height - 20;
      }
      if (newPosition.left + childRect.width > window.innerWidth - 20) {
        newPosition.left = window.innerWidth - childRect.width - 20;
      }

      setPosition(newPosition);
      return;
    }

    // Desktop positioning logic
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const childHeight = childRect.height;

    // Determine if popup should appear above or below
    let shouldShowAbove = placement === 'top';
    if (placement === 'auto') {
      shouldShowAbove = spaceBelow < childHeight && spaceAbove > spaceBelow;
    }

    // Calculate new position
    const newPosition = {
      top: shouldShowAbove
        ? triggerRect.top - childHeight - offset
        : triggerRect.bottom + offset,
      left: triggerRect.left,
    };

    // Adjust horizontal position if it would go off-screen
    if (newPosition.left + childRect.width > window.innerWidth) {
      newPosition.left = window.innerWidth - childRect.width - offset;
    }

    // Ensure it doesn't go off the left side
    newPosition.left = Math.max(offset, newPosition.left);

    if (newPosition.left !== 0 && newPosition.top !== 0) {
      setPosition(newPosition);
    }
  }, [placement, offset, triggerRef]);

  // Update position whenever dependencies change
  useEffect(() => {
    if (isOpen) {
      // Initial position update
      updatePosition();

      // Update position after a brief delay to ensure child has rendered
      const timeoutId = setTimeout(updatePosition, 0);

      const handleChange = () => {
        requestAnimationFrame(updatePosition);
      };

      window.addEventListener('resize', handleChange);
      window.addEventListener('scroll', handleChange, true);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleChange);
        window.removeEventListener('scroll', handleChange, true);
      };
    }
  }, [isOpen, updatePosition]);

  if (!isOpen || !portalRoot) return null;

  const isMobile = window.innerWidth <= 768;

  return createPortal(
    <>
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: zIndex - 1,
            backdropFilter: 'blur(2px)',
          }}
          onClick={onClose}
        />
      )}

      <div
        ref={childRef}
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex,
          opacity: position.top === 0 && position.left === 0 ? 0 : 1, // opacity condition added to prevent flicker effect
        }}
      >
        {children}
      </div>
    </>,
    portalRoot
  );
};

export default Portal;
